/**
 * Reference-identity registry (docs-internal/REFERENCE_IDENTITY.md).
 *
 * Serialized references are RELATIVE PATH STRINGS, which encode scene topology
 * at authoring time — every rename/reparent/wrap strands them. This registry
 * inverts the model so no mutation surface needs a repoint bracket:
 *
 *   - IDENTITY IS CAPTURED WHILE PATHS ARE VALID: at install, and after
 *     behavior commits (the `editor:behavior-changed` event — fired by the undo
 *     commands, including their undo/redo, always AFTER the commit settled).
 *     Unresolvable paths are simply not tracked until they resolve.
 *   - PATHS ARE DERIVED FROM IDENTITY ON EVERY TOPOLOGY CHANGE: the core
 *     `TraceScene.onTopologyChanged` signal schedules a debounced pass that
 *     re-derives each tracked path from its live `SceneObjectRef` and commits
 *     data whose strings changed.
 *
 * Derivation commits are NOT undo commands, by design: undoing the structural
 * change fires the same signal, and derivation restores the old strings.
 *
 * **Reference kinds (generalized).** The registry is parameterized over a set
 * of {@link ReferenceKind}s — each describes how to discover and write
 * the path references a behavior type serializes. Today: BehaviorGraph inline
 * `configuration.target` sockets, and Pages `pageStates` / `matchTransitions`
 * TransformPaths (P-1/P-2). A location within an owner is identified by an
 * opaque string key (a node index for graphs, `pageIndex\0path` for Pages), so
 * the tracking model does not assume any one shape.
 *
 * `$`-keyword paths are context-relative and never tracked; dynamic node-ref
 * targets are out of scope.
 */

import type { SceneObject } from '../../core/SceneObject';
import type { SceneObjectRef, SceneObjectIdentityRegistry } from '../../core/SceneObjectIdentity';
import { dispatchBehaviorChanged, EDITOR_EVENTS, type BehaviorChangedDetail } from '../undo/editor-events';
import { Logger } from '../../utils/Logger';
import { behaviorGraphReferenceKind } from './referenceKinds/behaviorGraphKind';
import { pagesReferenceKind } from './referenceKinds/pagesKind';

/** Minimal TraceScene surface used for object-path resolution. */
export interface TraceSceneLike {
    getSceneObjects(): SceneObject[];
    resolveSceneObject(path: string, context?: SceneObject): SceneObject | null;
    getRelativePath(target: SceneObject, context: SceneObject | null): string;
}

/**
 * One serialized path reference discovered on an owner: a stable location key
 * (unique within the owner, for one kind) and the current path string.
 */
export interface ReferenceLocation {
    /** Stable, kind-specific key for this reference within the owner. */
    key: string;
    /** The current serialized path string. */
    path: string;
}

/**
 * Describes how one behavior type serializes path references, so the registry
 * can capture their identity and re-derive their strings without knowing the
 * behavior's data shape. Implementations are pure (read/clone/commit only).
 */
export interface ReferenceKind {
    /** The `@type` used to match `editor:behavior-changed` events. */
    readonly behaviorType: string;
    /**
     * Enumerate every trackable path reference on the owner. Skip `$`-keyword
     * and empty/dynamic references. Returns [] when the owner lacks the
     * behavior. `key` must be stable across edits that don't remove the
     * reference (so unchanged entries keep their captured identity).
     */
    discover(owner: SceneObject): ReferenceLocation[];
    /**
     * Rewrite the paths at the given locations and commit. Called only when at
     * least one path changed. Returns a promise; a rejection leaves tracking
     * honest (the registry does not advance `lastPath` on failure).
     */
    write(owner: SceneObject, updates: Array<{ key: string; path: string }>): Promise<void>;
}

/** Relative path from `context` to `target` — the serialized reference form. */
function relativePathTo(
    target: SceneObject,
    traceScene: TraceSceneLike,
    context: SceneObject | null,
): string {
    if (context && typeof traceScene.getRelativePath === 'function') {
        return traceScene.getRelativePath(target, context);
    }
    return target.name;
}

/** The scene surface the registry needs (structural, test-fakeable). */
export interface ReferenceIdentityHost extends TraceSceneLike {
    onTopologyChanged(listener: () => void): () => void;
    objectIdentityRegistry: Pick<SceneObjectIdentityRegistry, 'refFor'>;
}

/** One tracked reference: what it points at, the string last seen, and its kind. */
interface TrackedTarget {
    targetRef: SceneObjectRef;
    lastPath: string;
    kind: ReferenceKind;
}

/** Live SceneObject behind a ref, or null if unbound/disposed. */
function liveOrNull(ref: SceneObjectRef): SceneObject | null {
    const obj = ref.get();
    if (!obj) return null;
    const transform = (obj as { transform?: { isDisposed?: () => boolean } }).transform;
    if (typeof transform?.isDisposed === 'function' && transform.isDisposed()) return null;
    return obj;
}

export class ReferenceIdentityRegistry {
    /** ownerRef → locationKey → tracked target. Keyed by shared identity refs
     * so entries survive delete/undo recreation of either endpoint. */
    private readonly _tracked = new Map<SceneObjectRef, Map<string, TrackedTarget>>();
    private readonly _scene: ReferenceIdentityHost;
    private readonly _kinds: ReferenceKind[];
    private readonly _unsubscribers: Array<() => void> = [];

    private _passScheduled = false;
    private _passRunning = false;
    private _passAgain = false;

    constructor(scene: ReferenceIdentityHost, kinds: ReferenceKind[]) {
        this._scene = scene;
        this._kinds = kinds;
    }

    /** Subscribe to topology + commit signals and capture the scene. */
    install(): void {
        if (typeof this._scene.onTopologyChanged !== 'function'
            || typeof this._scene.getSceneObjects !== 'function'
            || typeof this._scene.resolveSceneObject !== 'function') {
            Logger.warn('[referenceIdentity] scene lacks required APIs — registry inert');
            return;
        }
        this._unsubscribers.push(this._scene.onTopologyChanged(() => this._schedulePass()));

        const trackedTypes = new Set(this._kinds.map(k => k.behaviorType));
        const onBehaviorChanged = (e: Event): void => {
            const detail = (e as CustomEvent<BehaviorChangedDetail>).detail;
            if (!detail?.behaviorType || !trackedTypes.has(detail.behaviorType)) return;
            // Event is microtask-deferred post-commit, so data is settled.
            const owner = this._scene.getSceneObjects().find((o) => o.getPath() === detail.objectPath);
            if (owner) this.captureOwner(owner);
        };
        if (typeof window !== 'undefined') {
            window.addEventListener(EDITOR_EVENTS.BEHAVIOR_CHANGED, onBehaviorChanged);
            this._unsubscribers.push(() =>
                window.removeEventListener(EDITOR_EVENTS.BEHAVIOR_CHANGED, onBehaviorChanged));
        }

        this.captureAll();
    }

    uninstall(): void {
        for (const unsub of this._unsubscribers.splice(0)) unsub();
        this._tracked.clear();
    }

    /** Capture every owner in the scene. Paths must be VALID when this runs. */
    captureAll(): void {
        for (const owner of this._scene.getSceneObjects()) {
            this.captureOwner(owner);
        }
    }

    /**
     * (Re)capture one owner's references across all kinds. Entries whose string
     * is unchanged keep their identity; new/edited strings are resolved fresh;
     * unresolvable ones drop out of tracking until they resolve on a later pass.
     * Location keys are namespaced by kind so two kinds never collide.
     */
    captureOwner(owner: SceneObject): void {
        const ownerRef = this._scene.objectIdentityRegistry.refFor(owner);
        const previous = this._tracked.get(ownerRef);
        const entries = new Map<string, TrackedTarget>();

        for (const kind of this._kinds) {
            let locations: ReferenceLocation[];
            try {
                locations = kind.discover(owner);
            } catch {
                continue;
            }
            for (const loc of locations) {
                if (loc.path.startsWith('$')) continue;
                const mapKey = `${kind.behaviorType} ${loc.key}`;
                const existing = previous?.get(mapKey);
                if (existing && existing.lastPath === loc.path) {
                    entries.set(mapKey, existing);
                    continue;
                }
                const resolved = this._scene.resolveSceneObject(loc.path, owner);
                if (resolved) {
                    entries.set(mapKey, {
                        targetRef: this._scene.objectIdentityRegistry.refFor(resolved),
                        lastPath: loc.path,
                        kind,
                    });
                }
            }
        }

        if (entries.size > 0) this._tracked.set(ownerRef, entries);
        else this._tracked.delete(ownerRef);
    }

    /** Coalesce bursts (loads, composite undos) into one pass per microtask. */
    private _schedulePass(): void {
        if (this._passScheduled) return;
        this._passScheduled = true;
        queueMicrotask(() => {
            this._passScheduled = false;
            void this._runPass();
        });
    }

    private async _runPass(): Promise<void> {
        if (this._passRunning) {
            this._passAgain = true;
            return;
        }
        this._passRunning = true;
        try {
            await this._deriveAll();
            this.captureAll();
        } catch (err) {
            Logger.warn('[referenceIdentity] derive pass failed:', err);
        } finally {
            this._passRunning = false;
            if (this._passAgain) {
                this._passAgain = false;
                this._schedulePass();
            }
        }
    }

    /** Re-derive every tracked path from its live ref; commit changed owners. */
    private async _deriveAll(): Promise<void> {
        for (const [ownerRef, entries] of this._tracked) {
            const owner = liveOrNull(ownerRef);
            // Owner deleted: keep the entry so an undo-recreate (rebinding the
            // shared ref) resumes healing. Bounded retention trade-off (see spec).
            if (!owner) continue;

            // Group healed updates per kind so each kind commits once.
            const perKind = new Map<ReferenceKind, Array<{ key: string; path: string; tracked: TrackedTarget }>>();
            // Local key (strip the kind namespace) for the lookup and the write.
            const localKey = (mapKey: string, kind: ReferenceKind): string =>
                mapKey.slice(kind.behaviorType.length + 1);

            // Each kind's paths are read once per owner: a deck tracks a target
            // per page, and reading its data per target made every pass quadratic.
            const pathsByKind = new Map<ReferenceKind, Map<string, string> | null>();
            const pathsOf = (kind: ReferenceKind): Map<string, string> | null => {
                if (!pathsByKind.has(kind)) {
                    let paths: Map<string, string> | null;
                    try {
                        paths = new Map(kind.discover(owner).map(loc => [loc.key, loc.path]));
                    } catch {
                        paths = null;
                    }
                    pathsByKind.set(kind, paths);
                }
                return pathsByKind.get(kind)!;
            };

            for (const [mapKey, tracked] of entries) {
                const kind = tracked.kind;
                const target = liveOrNull(tracked.targetRef);
                if (!target) continue; // dangling — leave the last string in place

                const key = localKey(mapKey, kind);
                const current = pathsOf(kind)?.get(key);
                if (current == null) continue; // location removed/edited; commit re-captures
                const newPath = relativePathTo(target, this._scene, owner);
                if (current === newPath) {
                    tracked.lastPath = newPath;
                    continue;
                }
                // Only heal what actually BROKE: a string that still resolves to
                // the tracked target is left untouched (legacy bare-name search).
                if (current === tracked.lastPath
                    && this._scene.resolveSceneObject(current, owner) === target) {
                    continue;
                }
                if (current !== tracked.lastPath) {
                    // Author changed the string since capture — treat as intent.
                    const resolved = this._scene.resolveSceneObject(current, owner);
                    if (resolved) {
                        tracked.targetRef = this._scene.objectIdentityRegistry.refFor(resolved);
                        tracked.lastPath = current;
                    } else {
                        entries.delete(mapKey);
                    }
                    continue;
                }
                let list = perKind.get(kind);
                if (!list) { list = []; perKind.set(kind, list); }
                list.push({ key, path: newPath, tracked });
            }

            for (const [kind, updates] of perKind) {
                if (updates.length === 0) continue;
                try {
                    await kind.write(owner, updates.map(u => ({ key: u.key, path: u.path })));
                    for (const u of updates) u.tracked.lastPath = u.path;
                    // Deferred notification so panels re-read the healed targets.
                    dispatchBehaviorChanged({
                        objectPath: owner.getPath(),
                        behaviorType: kind.behaviorType,
                        action: 'updated',
                    });
                } catch (err) {
                    Logger.warn(`[referenceIdentity] failed to heal ${kind.behaviorType} targets on ${owner.name}:`, err);
                }
            }
        }
    }
}

/** The reference kinds tracked by default (BehaviorGraph targets + Pages paths). */
export const DEFAULT_REFERENCE_KINDS: ReferenceKind[] = [
    behaviorGraphReferenceKind,
    pagesReferenceKind,
];

/**
 * Create, install, and return the registry for a scene. Host calls this once
 * when the editor session starts. `uninstall()` for teardown/tests.
 */
export function installReferenceIdentity(
    scene: ReferenceIdentityHost,
    kinds: ReferenceKind[] = DEFAULT_REFERENCE_KINDS,
): ReferenceIdentityRegistry {
    const registry = new ReferenceIdentityRegistry(scene, kinds);
    registry.install();
    return registry;
}
